import type { User } from 'firebase/auth';
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { blockService } from '@/services';

// Shared empty array — `?? []` inline would mint a new identity every render
// while data is undefined, churning every memo and effect downstream
// (including the discovery feed subscription).
const EMPTY_UIDS: string[] = [];

export function useBlocks(user: User | null) {
  const queryClient = useQueryClient();

  // Query for blocked users data (filled by subscription below)
  const { data: blockedUidsData } = useQuery<string[]>({
    queryKey: ['blockedUids', user?.uid],
    // placeholder only; subscription fills the cache
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
  });

  // Query for blocked-by users data (filled by subscription below)
  const { data: blockedByUidsData } = useQuery<string[]>({
    queryKey: ['blockedByUids', user?.uid],
    // placeholder only; subscription fills the cache
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
  });

  // Set up real-time subscription for blocked users
  useEffect(() => {
    if (!user) return;

    const unsubscribe = blockService.subscribeToBlockedUsers(user.uid, updatedBlockedUids => {
      queryClient.setQueryData(['blockedUids', user.uid], updatedBlockedUids);
    });

    return () => unsubscribe();
  }, [user, queryClient]);

  // Set up real-time subscription for blocked by users
  useEffect(() => {
    if (!user) return;

    const unsubscribe = blockService.subscribeToBlockedByUsers(user.uid, updatedBlockedByUids => {
      queryClient.setQueryData(['blockedByUids', user.uid], updatedBlockedByUids);
    });

    return () => unsubscribe();
  }, [user, queryClient]);

  const blockUser = async (blockedUid: string) => {
    if (!user) throw new Error('User not authenticated');
    try {
      await blockService.blockUser(user.uid, blockedUid);
    } catch (err) {
      console.error('Block relationship failure:', err);
      throw err;
    }
  };

  const unblockUser = async (blockedUid: string) => {
    if (!user) throw new Error('User not authenticated');
    try {
      await blockService.unblockUser(user.uid, blockedUid);
    } catch (err) {
      console.error('Unblock relationship failure:', err);
      throw err;
    }
  };

  return {
    blockedUids: blockedUidsData ?? EMPTY_UIDS,
    blockedByUids: blockedByUidsData ?? EMPTY_UIDS,
    isLoading: Boolean(user && (blockedUidsData === undefined || blockedByUidsData === undefined)),
    blockUser,
    unblockUser,
  };
}
